import { Button, Spinner } from "@chakra-ui/react";
import React from "react";
import Card from "components/card";
import AppModal from "components/modal/AppModal";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { dteBooksApi } from "api/endpoints";
import { useShallow } from "zustand/react/shallow";
import { useEmissionAmbienteStore } from "stores/emissionAmbienteStore";
import ColumnSortHeader from "views/admin/tables/components/ColumnSortHeader";
import ServerTablePagination from "views/admin/tables/components/ServerTablePagination";

const columnHelper = createColumnHelper();

function formatDate(value) {
  if (value == null || value === "") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-CL", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

const LIST_FILTERS_INITIAL = () => ({
  book_type: "",
  lifecycle_status: "",
  modo: "",
  rut_emisor: "",
  period_tributario: "",
  limit: 50,
  sort_by: "created_at",
  sort_dir: "desc",
});

const LIMIT_OPTIONS = [50, 100, 200];
const BOOK_TYPE_OPTS = [
  { value: "", label: "Todos los tipos" },
  { value: "ventas", label: "Ventas" },
  { value: "compras", label: "Compras" },
  { value: "boletas", label: "Boletas" },
  { value: "guias", label: "Guías" },
];
const LIFECYCLE_OPTS = [
  { value: "", label: "Todos" },
  { value: "draft", label: "Borrador" },
  { value: "rendered", label: "Renderizado" },
  { value: "sent", label: "Enviado" },
  { value: "accepted", label: "Aceptado" },
  { value: "rejected", label: "Rechazado" },
  { value: "error", label: "Error" },
];
const MODO_OPTS = [
  { value: "", label: "Todos" },
  { value: "certificacion", label: "Certificación" },
  { value: "produccion", label: "Producción" },
];

function previewBookJson(item) {
  if (!item || typeof item !== "object") return "";
  const heavy = new Set([
    "payload_original",
    "payload_normalized",
    "summary_snapshot",
    "response_snapshot",
    "sii_response",
    "documentos",
  ]);
  const lite = {};
  for (const [k, v] of Object.entries(item)) {
    if (heavy.has(k)) {
      lite[k] = v != null ? "[…]" : null;
    } else {
      lite[k] = v;
    }
  }
  return JSON.stringify(lite, null, 2);
}

export default function DteMonthlyBooksTable() {
  const emission = useEmissionAmbienteStore(
    useShallow((s) => ({
      kind: s.kind,
      ambiente: s.ambiente,
      rutEmisor: s.rutEmisor,
    })),
  );
  const selectedAmbiente =
    emission.kind === "ready" && emission.ambiente ? emission.ambiente : "";
  const selectedRutEmisor =
    emission.kind === "ready" && emission.rutEmisor ? String(emission.rutEmisor).trim() : "";
  const [listFilters, setListFilters] = React.useState(LIST_FILTERS_INITIAL);
  const [page, setPage] = React.useState(1);

  const [data, setData] = React.useState([]);
  const [listMeta, setListMeta] = React.useState({
    total: 0,
    total_pages: 0,
    page: 1,
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailJson, setDetailJson] = React.useState("");

  const applyColumnSort = React.useCallback((key, dir) => {
    setPage(1);
    setListFilters((f) => ({ ...f, sort_by: key, sort_dir: dir }));
  }, []);

  const patchListFilters = React.useCallback((patch) => {
    setPage(1);
    setListFilters((f) => ({ ...f, ...patch }));
  }, []);

  const fetchList = React.useCallback(async () => {
    if (!selectedAmbiente) {
      setLoading(false);
      setData([]);
      setListMeta({ total: 0, total_pages: 0, page: 1 });
      return;
    }
    setLoading(true);
    setError("");
    try {
      const query = {
        limit: Math.min(200, Math.max(1, Number(listFilters.limit) || 50)),
        page: Math.max(1, page),
        sort_by: String(listFilters.sort_by || "created_at"),
        sort_dir: String(listFilters.sort_dir || "desc"),
      };
      const bt = String(listFilters.book_type ?? "").trim();
      if (bt) query.book_type = bt;
      const lc = String(listFilters.lifecycle_status ?? "").trim();
      if (lc) query.lifecycle_status = lc;
      const md = String(selectedAmbiente).trim();
      if (md) query.modo = md;
      const rut = String(listFilters.rut_emisor ?? "").trim();
      if (rut) query.rut_emisor = rut;
      const pt = String(listFilters.period_tributario ?? "").trim();
      if (pt) query.period_tributario = pt;

      const { res, data: payload } = await dteBooksApi.listMonthly(query);
      if (!res.ok || !payload?.ok) {
        setError(payload?.error || payload?.message || `Error ${res.status}`);
        setData([]);
        setListMeta({ total: 0, total_pages: 0, page: 1 });
        return;
      }
      setData(Array.isArray(payload.items) ? payload.items : []);
      setListMeta({
        total: Number(payload?.total ?? 0),
        total_pages: Number(payload?.total_pages ?? 0),
        page: Number(payload?.page ?? page),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error de red.");
      setData([]);
      setListMeta({ total: 0, total_pages: 0, page: 1 });
    } finally {
      setLoading(false);
    }
  }, [listFilters, page, selectedAmbiente]);

  React.useEffect(() => {
    fetchList();
  }, [fetchList]);

  React.useEffect(() => {
    if (!selectedAmbiente) return;
    setListFilters((f) => {
      if (f.modo === selectedAmbiente) return f;
      return { ...f, modo: selectedAmbiente };
    });
  }, [selectedAmbiente]);

  React.useEffect(() => {
    if (!selectedRutEmisor) return;
    setListFilters((f) => {
      if (String(f.rut_emisor || "").trim() === selectedRutEmisor) return f;
      return { ...f, rut_emisor: selectedRutEmisor };
    });
  }, [selectedRutEmisor]);

  const pageSize = Math.min(200, Math.max(1, Number(listFilters.limit) || 50));

  const openDetail = React.useCallback(async (id) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailJson("");
    try {
      const { res, data } = await dteBooksApi.getMonthly(id);
      if (!res.ok || !data?.ok || !data.item) {
        setDetailJson("");
        return;
      }
      setDetailJson(previewBookJson(data.item));
    } catch {
      setDetailJson("");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const columns = React.useMemo(
    () => [
      columnHelper.accessor("id", {
        header: () => (
          <ColumnSortHeader
            label="ID"
            sortKey="id"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <button
            type="button"
            className="text-sm font-medium text-brand-600 underline-offset-2 hover:underline dark:text-brand-300"
            onClick={() => openDetail(info.getValue())}
          >
            {info.getValue()}
          </button>
        ),
      }),
      columnHelper.accessor("internal_id", {
        header: () => (
          <ColumnSortHeader
            label="Internal ID"
            sortKey="internal_id"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="max-w-[160px] truncate font-mono text-xs text-navy-700 dark:text-white">
            {info.getValue() ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor("book_type", {
        header: () => (
          <ColumnSortHeader
            label="Tipo"
            sortKey="book_type"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm capitalize text-navy-700 dark:text-white">
            {info.getValue() ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor("period_tributario", {
        header: () => (
          <ColumnSortHeader
            label="Período"
            sortKey="period_tributario"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm tabular-nums text-navy-700 dark:text-white">
            {info.getValue() ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor("rut_emisor", {
        header: () => (
          <ColumnSortHeader
            label="RUT emisor"
            sortKey="rut_emisor"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm text-navy-700 dark:text-white">
            {info.getValue() ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor("modo", {
        header: () => (
          <ColumnSortHeader
            label="Modo"
            sortKey="modo"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm text-navy-700 dark:text-white">
            {info.getValue() ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor("lifecycle_status", {
        header: () => (
          <ColumnSortHeader
            label="Estado"
            sortKey="lifecycle_status"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm text-navy-700 dark:text-white">
            {info.getValue() ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor("sii_track_id", {
        header: () => (
          <ColumnSortHeader
            label="Track SII"
            sortKey="sii_track_id"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="max-w-[120px] truncate font-mono text-xs text-navy-700 dark:text-white">
            {info.getValue() ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor("total_documents", {
        header: () => (
          <ColumnSortHeader
            label="Docs"
            sortKey="total_documents"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm tabular-nums text-navy-700 dark:text-white">
            {info.getValue() ?? "—"}
          </span>
        ),
      }),
      columnHelper.accessor("created_at", {
        header: () => (
          <ColumnSortHeader
            label="Creado"
            sortKey="created_at"
            activeSortBy={listFilters.sort_by}
            activeSortDir={listFilters.sort_dir}
            onSort={applyColumnSort}
          />
        ),
        cell: (info) => (
          <span className="text-sm text-navy-700 dark:text-white">
            {formatDate(info.getValue())}
          </span>
        ),
      }),
      columnHelper.display({
        id: "acc",
        header: "",
        cell: ({ row }) => (
          <Button
            size="xs"
            variant="outline"
            colorScheme="brand"
            onClick={() => openDetail(row.original.id)}
          >
            Ver
          </Button>
        ),
      }),
    ],
    [applyColumnSort, listFilters.sort_by, listFilters.sort_dir, openDetail],
  );

  const table = useReactTable({
    data,
    columns,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id),
  });

  const showBlockingLoading = loading && data.length === 0;

  return (
    <>
      <Card extra="w-full p-4 sm:p-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h4 className="text-lg font-bold text-navy-700 dark:text-white">
              Libros mensuales
            </h4>
            <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-gray-400">
              Cierres en{" "}
              <code className="rounded bg-gray-100 px-1 text-xs dark:bg-white/10">
                dte_monthly_books
              </code>{" "}
              (por ejemplo desde <code className="text-[10px]">monthly/send</code>). Paginación en
              servidor.
            </p>
          </div>
          <button
            type="button"
            onClick={() => fetchList()}
            disabled={loading}
            className="linear shrink-0 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400"
          >
            {loading ? "Actualizando…" : "Actualizar"}
          </button>
        </div>

        {showBlockingLoading ? (
          <div className="mt-8 flex flex-col items-center gap-3">
            <Spinner color="brand.500" size="lg" thickness="4px" />
            <p className="text-sm text-gray-600 dark:text-gray-400">Cargando…</p>
          </div>
        ) : null}

        {error ? (
          <p
            className="mt-4 rounded-lg border border-red-400/40 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-200"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        {!showBlockingLoading && !error ? (
          <>
            <div className="mt-2 rounded-xl border border-gray-200 bg-lightPrimary/50 p-4 dark:border-white/10 dark:bg-navy-900/30">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                Filtros
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                  Tipo libro
                  <select
                    value={listFilters.book_type}
                    onChange={(e) =>
                      patchListFilters({ book_type: e.target.value })
                    }
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
                  >
                    {BOOK_TYPE_OPTS.map((o) => (
                      <option key={o.value || "all"} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                  Estado
                  <select
                    value={listFilters.lifecycle_status}
                    onChange={(e) =>
                      patchListFilters({ lifecycle_status: e.target.value })
                    }
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
                  >
                    {LIFECYCLE_OPTS.map((o) => (
                      <option key={o.value || "all"} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                  Modo
                  <select
                    value={listFilters.modo}
                    onChange={(e) =>
                      patchListFilters({ modo: e.target.value })
                    }
                    disabled
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
                  >
                    {MODO_OPTS.map((o) => (
                      <option key={o.value || "all"} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                  RUT emisor
                  <input
                    type="text"
                    value={listFilters.rut_emisor}
                    placeholder={selectedRutEmisor || "Sin emisor activo"}
                    disabled
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                  Período (YYYY-MM)
                  <input
                    type="text"
                    value={listFilters.period_tributario}
                    onChange={(e) =>
                      patchListFilters({ period_tributario: e.target.value })
                    }
                    placeholder="2026-04"
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                  Filas / página
                  <select
                    value={listFilters.limit}
                    onChange={(e) =>
                      patchListFilters({ limit: Number(e.target.value) })
                    }
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800 dark:text-white"
                  >
                    {LIMIT_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div className="relative mt-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10">
              {loading && data.length > 0 ? (
                <div className="pointer-events-none absolute inset-0 z-[1] flex justify-center bg-white/40 pt-6 dark:bg-navy-950/50">
                  <span className="h-fit rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium shadow-sm dark:border-white/10 dark:bg-navy-800">
                    Actualizando…
                  </span>
                </div>
              ) : null}
              <table className="w-full min-w-[1100px] border-collapse">
                <thead>
                  {table.getHeaderGroups().map((hg) => (
                    <tr
                      key={hg.id}
                      className="border-b border-gray-200 bg-white dark:border-white/10 dark:bg-navy-900"
                    >
                      {hg.headers.map((header) => (
                        <th
                          key={header.id}
                          className="px-3 py-3 text-left text-xs font-bold uppercase text-gray-600 dark:text-gray-300"
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="px-3 py-8 text-center text-sm text-gray-500"
                      >
                        {listMeta.total === 0
                          ? "No hay libros mensuales con estos filtros."
                          : "Sin filas en esta página."}
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-gray-100 odd:bg-white even:bg-gray-50/80 dark:border-white/5 dark:odd:bg-navy-900/40 dark:even:bg-navy-900/20"
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className="px-3 py-2.5 align-middle">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <ServerTablePagination
              page={page}
              totalPages={listMeta.total_pages}
              total={listMeta.total}
              pageSize={pageSize}
              noun="libros mensuales"
              subtext="Ordenación en servidor."
              onPageChange={setPage}
            />
          </>
        ) : null}
      </Card>

      <AppModal
        isOpen={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setDetailJson("");
        }}
        title="Detalle libro mensual"
        size="xl"
      >
        {detailLoading ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">Cargando…</p>
        ) : null}
        {!detailLoading && !detailJson ? (
          <p className="text-sm text-red-600">No se pudo cargar el detalle.</p>
        ) : null}
        {!detailLoading && detailJson ? (
          <pre className="max-h-[70vh] overflow-auto rounded-lg bg-gray-50 p-4 text-xs dark:bg-navy-900 dark:text-gray-200">
            {detailJson}
          </pre>
        ) : null}
      </AppModal>
    </>
  );
}
